import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="sales-oncall"]',
        title: 'Lịch trực Sales',
        description:
            'Hiển thị lịch trực Sales trong ngày.\n• Xem ai đang trực để phân công lead\n• Click vào tên để xem thông tin chi tiết',
    },
    {
        selector: '[data-tour="leads-overview"]',
        title: 'Tổng quan Leads',
        description:
            'Thống kê nhanh số lượng leads:\n• Tổng leads\n• Leads đã phân loại\n• Leads chờ xử lý',
    },
    {
        selector: '[data-tour="lead-filters"]',
        title: 'Bộ lọc Leads',
        description:
            'Lọc leads theo nhiều tiêu chí:\n• Trạng thái (Warm/Cool/Unqualified)\n• Kênh tiếp cận\n• Người phụ trách\n• Thời gian',
        demo: 'lead-filters-overview',
    },
    {
        selector: '[data-tour="lead-tabs"]',
        title: 'Các tab danh sách',
        description:
            'Chuyển đổi giữa các danh sách:\n• Leads của tôi\n• Tất cả leads\n• Thống kê chi tiết\n\nDouble-click vào dòng để mở chi tiết.',
        demo: 'lead-detail-modal',
    },
    {
        selector: '[data-tour="add-contact-btn"]',
        title: 'Thêm Lead mới',
        description:
            'Tạo nguồn liên hệ marketing mới.\n• Nhập thông tin khách hàng\n• Phân công cho nhân viên phụ trách',
    },
    {
        selector: '[data-tour="import-btn"]',
        title: 'Import Leads',
        description:
            'Import nhiều leads từ file Excel.\n• Tải template mẫu\n• Upload file có sẵn dữ liệu',
    },
    {
        selector: '[data-tour="export-btn"]',
        title: 'Export Leads',
        description:
            'Xuất danh sách leads ra file Excel.\n• Xuất theo bộ lọc đang chọn\n• Dùng để báo cáo, phân tích',
    },
]

/**
 * Tour hook for Contact Source Marketing List page
 * Shows key features of the leads management interface
 */
export const useContactSourceListTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useContactSourceListTour
